import StatusLabel from 'components/shared/StatusLabel';
import React from 'react';
import { BasePaymentData, OrderPaymentData } from 'services/api/PaymentService';
import PaymentMethodType from 'store/enums/PaymentMethodType';
import PaymentStatus from 'store/enums/PaymentStatus';
import PaymentMethodResponse from 'store/types/PaymentMethodResponse';
import { BaseAddressFormValues, PaymentFormValues, PaymentItemsFormValues } from 'store/types/FormValues';
import Payment, { MultipleCompaniesOrderPayment, OrderPayment } from 'store/types/Payment';
import { PaymentAchData, PaymentCardData } from 'store/types/PaymentMethodsData';
import PaymentSummaryItem from 'store/types/PaymentSummaryItem';
import AmericanExpressIcon from 'assets/icons/cardAmericanExpress.png';
import DinersClubIcon from 'assets/icons/cardDinersClub.png';
import DiscoverIcon from 'assets/icons/cardDiscover.png';
import MasterCardIcon from 'assets/icons/cardMasterCard.png';
import VisaIcon from 'assets/icons/cardVisa.png';
import PaymentMethodCardType from 'store/enums/PaymentMethodCardType';
import PaymentMethodStatus from 'store/enums/PaymentMethodStatus';
import PaymentMethod, { CardPaymentMethodDetails } from 'store/types/PaymentMethod';
import { CardTypes } from 'store/types/TenantConfig';
import OrderType from 'store/enums/OrderType';
import paymentMethodStatusConfig from 'store/configs/PaymentMethodStatusConfig';
import paymentStatusConfig from 'store/configs/PaymentStatusConfig';
import { ALL_COMPANIES_DROPDOWN_KEY } from './Company';
import { ButtonGroupWithBadgeItem } from 'components/shared/ButtonGroupWithBadge';

export const defaultBaseAddressFormValues: BaseAddressFormValues = {
  useHomeAddress: true,
  billingAddress: undefined,
};

export const defaultPaymentFormValues: PaymentFormValues = {
  useHomeAddress: true,
  saveMethod: false,
  paymentMethodValid: false,
  paymentMethodType: PaymentMethodType.Card,
  autoRenewal: false,
  billingAddress: undefined,
  paymentMethodId: undefined,
  totalPrice: 0,
  card: { bin: null, exp: null, hash: null, number: null, type: null },
  check: { aba: null, account: null, hash: null, name: null },
  token: '',
};

export const preselectedOrdersStatePropName = 'orderIds';

export const DISCOUNT_LABEL = 'Discount';
export const CREDITS_LABEL = 'Credits';

export const getPrice = (value = 0): string =>
  `$${Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)}`;

export const getFormattedNumber = (value = 0): string =>
  `${Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)}`;

export const getPaymentStatus = (status?: PaymentStatus): PaymentStatus => status || PaymentStatus.Processing;

export const getPaymentStatusLabel = (status: PaymentStatus = PaymentStatus.Processing): React.ReactNode => {
  const { theme = 'grey', name = '' } = paymentStatusConfig[status];

  return <StatusLabel theme={theme} status={name} />;
};

export const getPaymentMethodStatusLabel = (status: PaymentMethodStatus): React.ReactNode => {
  const { theme = 'grey', name = '' } = paymentMethodStatusConfig[status];

  return <StatusLabel theme={theme} status={name} />;
};

export const getFormattedOrdersList = (
  list: OrderPayment[] = [],
  unpaidOrdersOnly = false,
  salesOrders = false
): OrderPayment[] => {
  const formattedList: OrderPayment[] = list.map((payment: OrderPayment) => ({
    ...payment,
    status: getPaymentStatus(payment.status),
    invoiceName:
      payment.invoiceName || (salesOrders ? `Sale Order ${payment.orderNumber}` : `Invoice ${payment.orderNumber}`),
  }));
  const formattedSalesOrders: OrderPayment[] = formattedList.filter(({ type }) => type === OrderType.SalesOrd);
  const formattedInvoices: OrderPayment[] = formattedList.filter(({ type }) => type === OrderType.CustInvc);
  return !unpaidOrdersOnly
    ? formattedList.filter(
        ({ status }) =>
          status === PaymentStatus.FullyBilled ||
          status === PaymentStatus.Paid ||
          status === PaymentStatus.PendingFulfillment
      )
    : salesOrders
    ? formattedSalesOrders.filter(({ status }) => status === PaymentStatus.PendingBilling)
    : formattedInvoices.filter(({ status }) => status !== PaymentStatus.Paid);
};

export const getCardPaymentData = ({ token, card }: PaymentMethodResponse): PaymentCardData => {
  return {
    paymentToken: token,
    cardholderName: card?.cardholderName ?? '',
    cardBin: card?.bin ?? '',
    cardExpDate: card?.exp ?? '',
    cardType: card?.type ?? '',
    cardId: card?.number ?? '',
  };
};

export const getPaymentMethodStatus = (paymentMethod: PaymentMethod): PaymentMethodStatus => {
  const { isDefault, details } = paymentMethod;
  const { cardExpDate } = details as CardPaymentMethodDetails;
  const currentDateYear = new Date().getFullYear();
  const currentDateMonth = new Date().getMonth() + 1;

  if (cardExpDate) {
    const expiredDateYear = Number(cardExpDate.substring(2)) + 2000;
    const expiredDateMonth = Number(cardExpDate.substring(0, 2));
    if (
      expiredDateYear < currentDateYear ||
      (expiredDateYear === currentDateYear && expiredDateMonth < currentDateMonth)
    ) {
      return PaymentMethodStatus.Expired;
    }
  }
  return isDefault ? PaymentMethodStatus.Default : PaymentMethodStatus.Active;
};

export const getAchPaymentData = ({ token, check }: PaymentMethodResponse): PaymentAchData => {
  return {
    paymentToken: token,
    accountNumber: check?.account ?? '',
    accountName: check?.name ?? '',
    routingCode: check?.aba ?? '',
  };
};

export const getPaymentData = (paymentForm: PaymentFormValues): BasePaymentData => {
  const {
    paymentMethodType,
    paymentMethodId,
    useHomeAddress,
    billingAddress,
    saveMethod,
    discountId,
    poNumber,
    credits,
    totalPrice,
    card,
    check,
    token,
  } = paymentForm;
  let result: BasePaymentData = {
    useHomeAddress,
    billingAddress,
    saveMethod,
    paymentMethodId,
    discountId,
    poNumber,
    credits,
  };

  if (token) {
    if (paymentMethodType === PaymentMethodType.Ach) {
      result = {
        ...result,
        ach: {
          paymentToken: token,
          accountNumber: check?.account ?? '',
          accountName: check?.name ?? '',
          routingCode: check?.aba ?? '',
        },
      };
    } else if (paymentMethodType === PaymentMethodType.Card) {
      result = {
        ...result,
        creditCard: {
          paymentToken: token,
          cardBin: card?.bin ?? '',
          cardExpDate: card?.exp ?? '',
          cardType: card?.type ?? '',
          cardId: card?.number ?? '',
          cardholderName: card?.cardholderName ?? '',
        },
      };
    }
  } else if (paymentMethodType === PaymentMethodType.Check) {
    result = {
      ...result,
      check: {
        type: 'check',
      },
    };
  } else if (paymentMethodType === PaymentMethodType.Postpone) {
    result = {
      ...result,
      billMe: {
        type: 'billme',
      },
    };
  } else if (!totalPrice && credits) {
    result = {
      ...result,
      creditMemo: {
        type: 'creditMemo',
      },
    };
  }
  return result;
};

export const donationsAmountSelected = (donations: PaymentItemsFormValues): boolean =>
  !Object.keys(donations).length || Object.keys(donations).every((key: string) => donations[key].price !== undefined);

export const paymentFormInvalid = ({
  useHomeAddress,
  totalPrice = 0,
  billingAddress,
  paymentMethodValid,
}: PaymentFormValues): boolean =>
  (!useHomeAddress && billingAddress === undefined) || (totalPrice > 0 && !paymentMethodValid);

/**
 * Guards CollectJS init code before converting to NMI's React components
 *
 * @remarks
 * Created when we used CollectJs and processing payments took both server side processing and a client side request, this would guard the execution of the client-side request to get a credit card token. At time of writning, I'm not clear if we should remove this entirely in favor of {@link shouldExecutePaymentProcessing}
 */
export const shouldValidatePaymentMethod = ({
  paymentMethodType,
  paymentMethodId,
  totalPrice,
}: PaymentFormValues): boolean =>
  !(paymentMethodType === PaymentMethodType.Check || paymentMethodType === PaymentMethodType.Postpone) &&
  !(totalPrice === 0 || !!paymentMethodId);

/**
 * Guards submitting to backend seervices
 *
 * @remarks
 * Created after converting to NMI's React components, {@link shouldValidatePaymentMethod} does not behave correctly when using credits in the {@link MakePaymentPageView} component, as the total price is summed to 0 however there is no {@link paymentMethodId} (which appears to be the saved payment method to pay with, not the payment method id)
 */
export const shouldExecutePaymentProcessing = ({
  paymentMethodType,
  paymentMethodId,
  totalPrice,
}: PaymentFormValues): boolean => {
  if (paymentMethodType === PaymentMethodType.Check || paymentMethodType === PaymentMethodType.Postpone) {
    return true;
  }

  if (paymentMethodId) {
    return true;
  }

  if (totalPrice === 0) {
    return true;
  }

  return totalPrice > 0;
};

export const getSummaryTotalPrice = (summaryList: PaymentSummaryItem[] = []): number =>
  summaryList.reduce((sum, { name, price = 0 }) => {
    if (name === DISCOUNT_LABEL || name === CREDITS_LABEL) {
      const result = sum - price;

      return result > 0 ? result : 0;
    } else {
      return sum + price;
    }
  }, 0);
export const getFormattedPayment = (payment: Payment, isProcessing?: boolean): Payment => ({
  ...payment,
  status: getPaymentStatus(isProcessing ? undefined : payment.status),
});

export const getPaymentCardTypeConfig = (type: PaymentMethodCardType | string): { icon?: string; text: string } => {
  switch (type) {
    case PaymentMethodCardType.Visa:
      return { icon: VisaIcon, text: 'Visa' };
    case PaymentMethodCardType.Mastercard:
      return { icon: MasterCardIcon, text: 'MasterCard' };
    case PaymentMethodCardType.Discover:
      return { icon: DiscoverIcon, text: 'Discover' };
    case PaymentMethodCardType.Amex:
      return { icon: AmericanExpressIcon, text: 'American Express' };
    case PaymentMethodCardType.Diners:
      return { icon: DinersClubIcon, text: 'Diners Club' };
    case PaymentMethodCardType.Jcb:
      return { icon: undefined, text: 'JCB' };
    case PaymentMethodCardType.Maestro:
      return { icon: undefined, text: 'Maestro' };
    default:
      return { icon: undefined, text: type };
  }
};

export const getEnabledPaymentCardTypes = (cardTypes: CardTypes | undefined): PaymentMethodCardType[] => {
  if (!cardTypes) {
    return Object.values(PaymentMethodCardType) as PaymentMethodCardType[];
  }

  const validCardTypes = Object.values(PaymentMethodCardType) as PaymentMethodCardType[];

  return Object.entries(cardTypes)
    .filter(([, enabled]) => enabled)
    .map(([type]) => {
      const normalized = validCardTypes.find((cardType) => cardType.toLowerCase() === type.toLowerCase());
      return normalized;
    })
    .filter((type): type is PaymentMethodCardType => Boolean(type));
};

export const getInvoiceModalDescription = (status?: PaymentStatus, defaultDescription?: boolean): React.ReactNode => {
  const description = 'Thank you, your payment has been successfully processed.';

  if (defaultDescription || !status) {
    return description;
  }
  switch (status) {
    case PaymentStatus.Paid:
      return 'Thank you, your payment has been successfully processed. Please see invoice details below.';
    case PaymentStatus.Unpaid:
    case PaymentStatus.Overdue:
      return (
        <>
          <span>{'Thank you for your order. '}</span>
          <span>{'Please see invoice details below. '}</span>
        </>
      );
    case PaymentStatus.PendingBilling:
      return (
        <>
          <span>{'Thank you for your order. '}</span>
          <span>{'Please see sales order details below. '}</span>
        </>
      );
    default:
      return description;
  }
};

export const getInvoicesData = (
  invoices: OrderPayment[],
  isMultipleCompanies?: boolean,
  salesOrders?: boolean
): OrderPaymentData['invoices'] => {
  if (isMultipleCompanies) {
    const result: MultipleCompaniesOrderPayment[] = [];
    invoices.forEach(({ ownerId, id }) => {
      if (!result.length && ownerId) {
        result.push({
          companyId: ownerId,
          invoicesId: salesOrders ? undefined : [id],
          salesOrdersId: salesOrders ? [id] : undefined,
        });
      } else {
        for (let i = 0; i < result.length; i++) {
          const { companyId, invoicesId } = result[i];
          if (ownerId === companyId) {
            invoicesId?.push(id);
            break;
          } else if (i === result.length - 1 && ownerId) {
            result.push({
              companyId: ownerId,
              invoicesId: salesOrders ? undefined : [id],
              salesOrdersId: salesOrders ? [id] : undefined,
            });
            break;
          }
        }
      }
    });
    return result;
  }
  return invoices.map(({ id }) => id);
};

export const getSubsidiaryButtonItems: (invoices?: OrderPayment[], companyId?: string) => ButtonGroupWithBadgeItem[] = (
  invoices?: OrderPayment[],
  companyId?: string
) => {
  if (!invoices) return [];
  const unpaidInvoices = getFormattedOrdersList(invoices, true);

  if (!unpaidInvoices) return [];

  const filteredUnpaidInvoices =
    !companyId || companyId === ALL_COMPANIES_DROPDOWN_KEY
      ? unpaidInvoices
      : unpaidInvoices.filter((item) => item.ownerId === companyId);

  const map = new Map<string, ButtonGroupWithBadgeItem>();

  for (const unpaidInvoice of filteredUnpaidInvoices) {
    const buttonLabel = (unpaidInvoice.subsidiaryDisplayName || unpaidInvoice.subsidiaryName) ?? '';

    if (!unpaidInvoice?.subsidiaryId || !buttonLabel) {
      continue;
    }

    const key = unpaidInvoice.subsidiaryId;
    const existing = map.get(key);

    if (existing?.count) {
      existing.count += 1;
    } else {
      const buttonItem: ButtonGroupWithBadgeItem = {
        id: unpaidInvoice.subsidiaryId,
        label: buttonLabel,
        count: 1,
      };
      map.set(key, buttonItem);
    }
  }

  return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
};
